import {
    LoginSuccessResponse,
    SocialLoginResponse,
    LoginSuccessResponseStatus,
    SocialLoginResponseStatus,
    SocialLoginConfig,
    SocialSdkConfig,
    ULPath,
    SocialType,
    SDKSettingsResponse,
    UserPreference,
    LoginSuccessAdditional,
    TransactionDataResponse,
    PasskeyRegistrationPlatformResponse,
    PasskeyAuthenticationPlatformResponse,
    PasskeyRegistrationExitState,
} from '@ul/common';
import type {
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { SDKEffectService } from '@services/sdkEffect/SDKEffectService';
import { Logger } from '@services/logger/Logger';
import { Measurement, TagBuilder, Tags } from '@services/measurement';
import { ULRpcCaller } from '@services/rpcCaller';

export class ULRpcSDKEffectService implements SDKEffectService {
    constructor(ulRpcCaller: ULRpcCaller, measurement: Measurement, logger: Logger, appId?: string) {
        this.ulRpcCaller = ulRpcCaller;
        this.logger = logger;
        this.measurement = measurement;
        this.appId = appId;
    }

    private readonly ulRpcCaller: ULRpcCaller;
    private readonly measurement: Measurement;
    private readonly logger: Logger;
    private readonly appId: string | undefined;

    public readonly onLoginSuccessEffect = (additional?: LoginSuccessAdditional): Promise<LoginSuccessResponse> => {
        return this.measureP<LoginSuccessResponse>(response => ({
            status: response ? LoginSuccessResponseStatus[response.loginStatus] : 'err',
            procedure: 'onLoginSuccess',
        }))(
            new Promise((resolve, reject) => {
                const logTags = {
                    callback: 'onLoginSuccessEffect',
                    appId: this.appId ? this.appId : 'unknown',
                };
                this.ulRpcCaller
                    .onLoginSuccess(additional)
                    .then(response => {
                        const { loginStatus, message } = response;
                        const messageStr = message?.success
                            ? message.success
                            : message?.error
                            ? message.error
                            : message?.info
                            ? message.info
                            : 'default';

                        this.logger.info(`RPC onLoginSuccess returned status ${messageStr}`, undefined, {
                            ...logTags,
                            status: `${loginStatus}`,
                        });
                        this.measurement.count('ul.sdk.rpc', 1, { ...logTags, success: true });
                        resolve(response);
                    })
                    .catch(error => {
                        this.logger.error('RPC onLoginSuccess error', error, logTags);
                        this.measurement.count('ul.sdk.rpc', 1, { ...logTags, success: false });
                        reject(error);
                    });
            })
        );
    };

    public readonly onSpaWillLoad = (): Promise<SDKSettingsResponse> => {
        return this.measureP<SDKSettingsResponse>(response => ({
            status: !!response ? 'success' : 'err',
            procedure: 'onSpaWillLoad',
        }))(
            new Promise((resolve, reject) => {
                this.ulRpcCaller
                    .onSpaWillLoad()
                    .then(response => {
                        this.logger.info(`RPC onSpaWillLoad is successful`);
                        resolve(response);
                    })
                    .catch(error => {
                        this.logger.error('RPC onSpaWillLoad error', error);
                        reject(error);
                    });
            })
        );
    };

    public readonly onWindowSizeChangedEffect = (height: number, width: number) => {
        this.ulRpcCaller.onWindowSizeChanged(height, width);
    };

    public readonly onLoginCompleteEffect = (isSuccess: boolean, userPreference?: UserPreference) => {
        this.ulRpcCaller.onLoginComplete(isSuccess, userPreference);
        this.logger.info(`RPC onLoginComplete - isSuccess: ${isSuccess}`);
    };

    public readonly onExit = () => {
        this.ulRpcCaller.onExit();
    };

    public readonly onSignout = () => {
        this.ulRpcCaller.onSignout();
    };

    public readonly onSpaLoad = (spaLoadCompletedAt: number) => {
        this.ulRpcCaller.onSpaLoad(spaLoadCompletedAt);
    };

    public readonly onRouteChange = (path: ULPath, isBack: boolean) => {
        this.ulRpcCaller.onRouteChange(path, isBack);
    };

    public readonly onNotificationPush = (message: string, isSuccess: boolean) => {
        this.ulRpcCaller.onNotificationPush(message, isSuccess);
    };

    public readonly onSocialSdkLoadEffect = (config: SocialSdkConfig): Promise<boolean> => {
        const callProcedure = async () => {
            try {
                const loaded = await this.ulRpcCaller.onSocialSdkLoad(config);
                return loaded;
            } catch (error) {
                this.logger.error('RPC onSocialSdkLoad error', error);
                return false;
            }
        };

        return this.measureP<boolean>(response => ({
            success: response,
            type: SocialType[config.type],
            procedure: 'onSocialSdkLoad',
        }))(callProcedure());
    };

    public readonly onSocialLoginEffect = (config: SocialLoginConfig): Promise<SocialLoginResponse> => {
        const callProcedure = async () => {
            const baseTags = {
                callback: 'onSocialLoginEffect',
                appId: !!this.appId ? this.appId : 'unknown',
                socialtype: SocialType[config.type],
            };
            try {
                const response = await this.ulRpcCaller.onSocialLogin(config);
                const { status } = response;
                const logTags = {
                    ...baseTags,
                    status: `${status}`,
                };
                this.logger.info(`RPC onSocialLogin returned status`, undefined, logTags);
                this.measurement.count('ul.sdk.rpc', 1, { ...logTags, success: true });
                return response;
            } catch (error) {
                this.logger.error('RPC onSocialLogin error', error, baseTags);
                this.measurement.count('ul.sdk.rpc', 1, { ...baseTags, success: false });
                throw error;
            }
        };

        return this.measureP<SocialLoginResponse>(response => ({
            status: response ? SocialLoginResponseStatus[response.status] : 'err',
            type: SocialType[config.type],
            procedure: 'onSocialLogin',
        }))(callProcedure());
    };

    public readonly onDeleteAccountSuccess = () => {
        this.ulRpcCaller.onDeleteAccountSuccess();
    };

    public readonly onDeleteAccountExit = () => {
        this.ulRpcCaller.onDeleteAccountExit();
    };

    public readonly onForceLogoutSuccess = () => {
        this.ulRpcCaller.onForceLogoutSuccess();
    };

    public readonly onForceLogoutExit = () => {
        this.ulRpcCaller.onForceLogoutExit();
    };

    public readonly onDismissButtonClick = () => {
        this.ulRpcCaller.onDismissButtonClick();
    };

    public readonly onVerifyOtp = (message: string, isSuccess: boolean, status: number) => {
        this.ulRpcCaller.onVerifyOtp(message, isSuccess, status);
    };

    public readonly onWalletAuthenticated = (success: boolean) => [this.ulRpcCaller.onWalletAuthenticated(success)];

    public readonly onTransactionWillLoad = () => {
        return this.measureP<TransactionDataResponse>(response => ({
            status: !!response ? 'success' : 'err',
            procedure: 'onTransactionWillLoad',
        }))(
            new Promise((resolve, reject) => {
                this.ulRpcCaller
                    .onTransactionWillLoad()
                    .then(response => {
                        this.logger.info(`RPC onTransactionWillLoad is successful`);
                        resolve(response);
                    })
                    .catch(error => {
                        this.logger.error('RPC onTransactionWillLoad error', error);
                        reject(error);
                    });
            })
        );
    };

    public readonly onPasskeyRegister = (
        challengeOptions: PublicKeyCredentialCreationOptionsJSON
    ): Promise<PasskeyRegistrationPlatformResponse> => {
        const callProcedure = async () => {
            const baseTags = {
                callback: 'onPasskeyRegister',
                appId: !!this.appId ? this.appId : 'unknown',
            };
            try {
                const response = await this.ulRpcCaller.onPasskeyRegister(challengeOptions);
                this.logger.info('RPC onPasskeyRegister success', undefined, baseTags);
                this.measurement.count('ul.sdk.rpc', 1, { ...baseTags, success: true });
                return response;
            } catch (error) {
                this.logger.error('RPC onPasskeyRegister error', error, baseTags);
                this.measurement.count('ul.sdk.rpc', 1, { ...baseTags, success: false });
                throw error;
            }
        };

        return this.measureP<PasskeyRegistrationPlatformResponse>(response => ({
            success: !!response?.success,
            procedure: 'onPasskeyRegister',
        }))(callProcedure());
    };

    public onPasskeyAuthenticate = (
        challengeOptions: PublicKeyCredentialRequestOptionsJSON,
        useConditionalMediation?: boolean
    ): Promise<PasskeyAuthenticationPlatformResponse> => {
        const callProcedure = async () => {
            try {
                return await this.ulRpcCaller.onPasskeyAuthenticate(challengeOptions, useConditionalMediation);
            } catch (err) {
                this.logger.error('onPasskeyAuthenticate error', err);
                throw err;
            }
        };

        return this.measureP<PasskeyAuthenticationPlatformResponse>(response => ({
            success: !!response?.success,
            procedure: 'onPasskeyAuthenticate',
        }))(callProcedure());
    };

    public onPasskeySupportsAutofill = (): Promise<boolean> => {
        const callProcedure = async () => {
            try {
                return await this.ulRpcCaller.onPasskeySupportsAutofill();
            } catch (err) {
                this.logger.error('onPasskeySupportsAutofill error', err);
                return false;
            }
        };

        return this.measureP<boolean>(response => ({
            success: response,
            procedure: 'onPasskeySupportsAutofill',
        }))(callProcedure());
    };

    public onPasskeyRegistrationExit = (exitState: PasskeyRegistrationExitState) => {
        this.ulRpcCaller.onPasskeyRegistrationExit(exitState);
    };

    private measureP = <TRes>(tags?: Tags | TagBuilder<TRes>) => (measured: Promise<TRes>) => {
        return this.measurement.measurePromise('ul.clientside.rpc', measured, tags);
    };
}
